import { TextField, TextFieldProps } from '@mui/material';
import { useRef, useState } from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import ReadOnlyField from './readonly-field';

interface PhoneNumberFieldProps<TFieldValues extends FieldValues> extends Omit<
  TextFieldProps,
  'name'
> {
  readonly name: Path<TFieldValues>;
  readonly control: Control<TFieldValues>;
  readonly formatPhoneNumber: (value: string) => string;
  readonly required?: boolean;
  readonly isReadOnly?: boolean;
  readonly label?: string;
}

/**
 * A reusable phone number input field that handles formatting
 * Supports both editable and read-only modes
 */
const PhoneNumberField = <TFieldValues extends FieldValues>({
  name,
  control,
  formatPhoneNumber,
  required = false,
  isReadOnly = false,
  label = 'Phone Number',
  ...textFieldProps
}: PhoneNumberFieldProps<TFieldValues>) => {
  const [isFocused, setIsFocused] = useState(false);
  const [rawValue, setRawValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Helper function to extract digits and common formatting characters
  const extractDigitsAndFormatting = (value: string) => {
    // Allow digits, dashes, dots, spaces, parentheses, and plus sign
    return value.replace(/[^\d\-.\s()+]/g, '');
  };

  // Helper function to extract only digits
  const extractDigitsOnly = (value: string) => {
    return value.replace(/\D/g, '');
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        // Only show error if field is required OR has a value and is invalid
        const showError = required ? !!fieldState.error : !!field.value && !!fieldState.error;

        // Determine what value to display
        const getDisplayValue = () => {
          if (isFocused) {
            // When focused, show the raw unformatted value
            return rawValue;
          } else {
            // When not focused, show formatted value if there's data
            return field.value ? formatPhoneNumber(field.value) : '';
          }
        };

        if (isReadOnly) {
          const displayValue = field.value ? formatPhoneNumber(field.value) : '';
          return <ReadOnlyField label={label} value={displayValue} />;
        }

        return (
          <TextField
            {...textFieldProps}
            ref={inputRef}
            fullWidth
            label={label}
            value={getDisplayValue()}
            onChange={e => {
              const inputValue = e.target.value;

              // Allow common formatting characters while typing
              const cleanedValue = extractDigitsAndFormatting(inputValue);
              setRawValue(cleanedValue);

              // Store only digits in the form state
              const digitsOnly = extractDigitsOnly(cleanedValue);

              // If empty and not required, allow empty value
              if (digitsOnly === '' && !required) {
                field.onChange('');
                return;
              }

              // Allow more than 10 digits temporarily to trigger validation
              // The validation schema will catch invalid lengths
              field.onChange(digitsOnly);
            }}
            onFocus={e => {
              setIsFocused(true);

              // If there's existing formatted data, convert it back to raw digits
              if (field.value) {
                const rawDigits = extractDigitsOnly(field.value);
                setRawValue(rawDigits);

                // Small delay to ensure the value is set before positioning cursor
                setTimeout(() => {
                  if (inputRef.current) {
                    inputRef.current.setSelectionRange(rawDigits.length, rawDigits.length);
                  }
                }, 0);
              } else {
                setRawValue('');
              }

              // Call original onFocus if provided
              if (textFieldProps.onFocus) {
                textFieldProps.onFocus(e);
              }
            }}
            onBlur={e => {
              setIsFocused(false);

              // Clear rawValue when losing focus
              setRawValue('');

              // Call original onBlur if provided
              if (textFieldProps.onBlur) {
                textFieldProps.onBlur(e);
              }

              // Trigger field onBlur for validation
              field.onBlur();
            }}
            error={showError}
            helperText={showError ? fieldState.error?.message : ''}
            placeholder={required ? '(123) 456-7890' : ''}
            required={required}
          />
        );
      }}
    />
  );
};

export default PhoneNumberField;
